import type { DroneAssetKind, DroneCapability, DroneEnemy, DroneRegime, MtoTarget, SmallDrones, SmallDronesCause, WeatherOptions } from "./api/types";
import { k, t } from "./i18n/i18n";

/** Emploi des drones (chantier « Drones ») : vocabulaire fermé de l'interface, au mot près celui du serveur
 *  (`drones_web.json`, D17), et lignes composées (capacité drone de l'ENI, état des petits drones). Module pur : il ne
 *  fait que présenter le plan que le serveur résout (`DronePlan`). */

/** Régimes par temps, du plus libre au plus restrictif (un temps ne peut être plus libre que celui du chef). */
export const REGIMES: readonly DroneRegime[] = ["libre", "sur_ordre", "interdit"];

const REGIME_LABELS: Record<DroneRegime, string> = {
  libre: k("Libre"),
  sur_ordre: k("Sur ordre"),
  interdit: k("Interdit"),
};

/** Libellé d'un régime, tel que les réglages et les messages le citent (« Sur ordre »). */
export function regimeLabel(regime: DroneRegime): string {
  return t(REGIME_LABELS[regime]);
}

/** Rang d'un régime (0 libre, 1 sur ordre, 2 interdit) : une option plus libre que le cadre du chef est bloquée. */
export function regimeRank(regime: DroneRegime): number {
  return REGIMES.indexOf(regime);
}

const ASSET_LABELS: Record<DroneAssetKind, string> = {
  micro: k("Micro-drones"),
  contact: k("Drones de contact"),
  mto: k("Munitions téléopérées"),
  tactical: k("Drones tactiques"),
};

/** Libellé d'une nature de moyen drone ami. */
export function assetLabel(kind: DroneAssetKind): string {
  return t(ASSET_LABELS[kind]);
}

/** Les dix cibles des munitions téléopérées, dans l'ordre du serveur. */
export const MTO_TARGETS: readonly { key: MtoTarget; label: string }[] = [
  { key: "cdt", label: k("Postes de commandement") },
  { key: "art", label: k("Artillerie et lance-roquettes") },
  { key: "sol_air", label: k("Défense sol-air") },
  { key: "genie", label: k("Engins du génie de brèche et de franchissement") },
  { key: "chars", label: k("Chars de bataille") },
  { key: "vci", label: k("Véhicules de combat d'infanterie") },
  { key: "antichar", label: k("Postes de tir antichars") },
  { key: "drones", label: k("Équipes de drones et télépilotes ennemis") },
  { key: "log", label: k("Moyens logistiques") },
  { key: "reco", label: k("Éléments de reconnaissance") },
];

/** Libellé d'une cible des munitions téléopérées ; clé inconnue : la clé telle quelle. */
export function mtoLabel(key: MtoTarget | string): string {
  const found = MTO_TARGETS.find((target) => target.key === key);
  return found ? t(found.label) : key;
}

/** Plafonds proposés du VAA (m sol) : de 600 à 1 000 m par pas de 100. */
export const VAA_STEPS: readonly number[] = [600, 700, 800, 900, 1000];

const CAPABILITY_LABELS: Record<DroneCapability, string> = {
  absent: k("absente"),
  faible: k("faible"),
  moyen: k("moyenne"),
  fort: k("forte"),
};

const CAPABILITY_SOURCES: Record<DroneEnemy["source"], string> = {
  reference: k("ENI de référence"),
  custom: k("fiche de l'ENI personnalisé"),
  generic: k("ennemi générique"),
};

/** « Capacité drone de l'ENI : moyenne » ; null sans menace connue (CENTAC, aperçu indisponible). */
export function capabilityLine(enemy: DroneEnemy | null | undefined): string | null {
  if (!enemy) return null;
  return t("Capacité drone de l'ENI : {level}", { level: t(CAPABILITY_LABELS[enemy.level]) });
}

/** Source de la capacité (« ENI de référence », « fiche de l'ENI personnalisé », « ennemi générique »). */
export function capabilitySource(enemy: DroneEnemy): string {
  return t(CAPABILITY_SOURCES[enemy.source]);
}

/** Vent dès lequel les petits drones sont interdits (`drones_web.json`, `wind_suspended_kmh`, recopié ici seulement ;
 *  un essai vérifie l'égalité) : seule cause citée, avec le givrage, quand ils le sont. */
export const WIND_SUSPENDED_KMH = 45;

/** Causes citées de l'état « petits drones » : toutes quand ils sont limités ; quand ils sont interdits, seulement le
 *  givrage et le vent dès `WIND_SUSPENDED_KMH` (les autres causes ne font que limiter). */
export function citedCauses(small: SmallDrones): SmallDronesCause[] {
  return small.status === "suspended"
    ? small.causes.filter((c) => c === "icing" || (c === "wind" && small.wind_kmh >= WIND_SUSPENDED_KMH))
    : [...small.causes];
}

function causeLabel(cause: SmallDronesCause, small: SmallDrones): string {
  switch (cause) {
    case "wind":
      return t("vent {kmh} km/h", { kmh: small.wind_kmh });
    case "icing":
      return t("givrage");
    case "rain":
      return t("pluie (autonomie réduite de moitié)");
    case "cold":
      return t("froid (batteries)");
    case "heat":
      return t("chaleur (batteries)");
  }
}

type Darkness = NonNullable<WeatherOptions["ephemeris"]>["darkness"];

/** Ligne « petits drones » de l'aperçu Météo, des modes d'action et de l'analyse (D8) : « Petits drones limités :
 *  vent 40 km/h, pluie (autonomie réduite de moitié). », « Petits drones interdits : vent 50 km/h. », suivie, par nuit
 *  sombre (`night_thermal`, ou `darkness.dark` de l'éphéméride), de « Nuit noire : caméra thermique exigée pour nos
 *  petits drones. » ; null quand rien ne les gêne. */
export function smallDronesEffect(small: SmallDrones | null | undefined, darkness?: Darkness): string | null {
  if (!small) return null;
  const lines: string[] = [];
  if (small.status !== "ok") {
    const why = citedCauses(small).map((c) => causeLabel(c, small)).join(", ");
    if (small.status === "suspended")
      lines.push(why ? t("Petits drones interdits : {causes}.", { causes: why }) : t("Petits drones interdits."));
    else lines.push(why ? t("Petits drones limités : {causes}.", { causes: why }) : t("Petits drones limités."));
  }
  if (small.night_thermal || darkness?.dark) lines.push(t("Nuit noire : caméra thermique exigée pour nos petits drones."));
  return lines.length ? lines.join(" ") : null;
}
